import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Optional, Resume } from '@rio/types'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  actions as editorActions,
  selectors as editorSelectors,
  actions,
  ResumeSectionId,
} from 'builder/modules/resumeEditor'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { SortableSection, groupSectionsBy } from 'builder/components/SectionsList'
import { SectionsListContainer } from 'builder/components/SectionsList/styles'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { GeneralEditorLevels } from 'builder/modules/generalEditor'
import {
  AddingOptions,
  CardOperationFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  UpdateCardFunc,
} from 'builder/components/Card'
import { DeleteSectionFunc, RenameSectionFunc } from 'builder/components/Section'
import { getJapaneseSectionsList } from './JapaneseSections'

type Props = {
  resume: Resume
  levels: GeneralEditorLevels
}

export const JapaneseSectionsList = (props: Props) => {
  const { resume, levels } = props
  const { hideSkillLevel, locale, sectionsOrder } = resume

  const sections = getJapaneseSectionsList(resume)
  const groups = groupSectionsBy(sections, 'sortable')
  const dispatch = useDispatch()
  const openedCard = useTypedSelector(editorSelectors.openedCard)
  const openedSections = useTypedSelector(editorSelectors.openedSections)

  const handleCardAdd = useCallback(
    (sectionId: ResumeSectionId, options?: AddingOptions) => {
      const cardId = generateRandomId()
      dispatch(
        actions.addCard({
          sectionName: sectionId,
          cardId,
          options: { scrollIntoViewport: options?.scrollIntoViewport ?? false, shouldOpen: true },
        }),
      )
    },
    [dispatch],
  )

  const isCardOpen = useCallback(
    (cardId: Optional<string | number>, sectionId: ResumeSectionId) => {
      if (!openedCard) return false

      return openedCard.id === cardId && openedCard.sectionId === sectionId
    },
    [openedCard],
  )

  const handleCardToggle = useCallback(
    (cardId: Optional<string | number>, sectionId: ResumeSectionId) => {
      const isOpen = isCardOpen(cardId, sectionId)
      dispatch(editorActions.openCard(isOpen ? null : { id: cardId, sectionId }))
    },
    [dispatch, isCardOpen],
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return

      const draggableId = active.id as ResumeSectionId
      const oldIndex = sectionsOrder.indexOf(active.id as string)
      const newIndex = sectionsOrder.indexOf(over.id as string)

      dispatch(
        actions.moveSection({
          offset: newIndex - oldIndex,
          sectionId: draggableId,
        }),
      )
    },
    [dispatch, sectionsOrder],
  )

  const updateCard: UpdateCardFunc = useCallback(
    (sectionId, cardId, values, debounce, isCustom) => {
      dispatch(actions.updateCard({ sectionId, cardId, values, debounce, isCustom }))
    },
    [dispatch],
  )

  const deleteCard: CardOperationFunc = useCallback(
    (cardId, sectionName, isCustom = false) => {
      dispatch(actions.deleteCard({ sectionName, cardId, isCustom }))
    },
    [dispatch],
  )

  const moveCard: MoveCardFunc = useCallback(
    payload => {
      dispatch(actions.moveCard(payload))
    },
    [dispatch],
  )

  const renameSection: RenameSectionFunc = useCallback(
    payload => {
      dispatch(actions.renameSection({ ...payload, debounce: true }))
    },
    [dispatch],
  )

  const deleteSection: DeleteSectionFunc = useCallback(
    (id, options) => {
      dispatch(actions.deleteSection({ id, options: { ...options } }))
    },
    [dispatch],
  )

  const handleSectionToggle = useCallback(
    (sectionId: ResumeSectionId) => {
      const isOpened = openedSections[sectionId] ?? true
      dispatch(editorActions.setOpenedSections({ [sectionId]: !isOpened }))
    },
    [dispatch, openedSections],
  )

  const addCardSubItem: CardOperationFunc = useCallback(
    (cardId, sectionId) => {
      dispatch(actions.addCardSubItem({ sectionName: sectionId, cardId }))
    },
    [dispatch],
  )

  const moveCardSubItem: MoveSubItemFunc = useCallback(
    payload => {
      dispatch(actions.moveCardSubItem(payload))
    },
    [dispatch],
  )

  return (
    <SectionsListContainer>
      {groups.map((group, groupIndex) => {
        const content = group.sections.map((section, sectionIndex) => (
          <SortableSection
            item={section}
            key={section.id}
            index={sectionIndex}
            isOpened={openedSections[section.id] ?? true}
            levels={levels}
            hideSkillLevel={hideSkillLevel}
            locale={locale}
            onRename={renameSection}
            onDelete={deleteSection}
            onAdd={handleCardAdd}
            isCardOpen={isCardOpen}
            onCardChange={updateCard}
            onCardDelete={deleteCard}
            onCardMove={moveCard}
            onCardToggle={handleCardToggle}
            onToggle={handleSectionToggle}
            onAddCardSubItem={addCardSubItem}
            onSubItemMove={moveCardSubItem}
          />
        ))

        if (!group.sortable) return <div key={groupIndex}>{content}</div>

        return (
          <DndContext key={groupIndex} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
            <SortableContext items={sectionsOrder} strategy={verticalListSortingStrategy}>
              {content}
            </SortableContext>
          </DndContext>
        )
      })}
    </SectionsListContainer>
  )
}
